import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './App.css';
import './i18n';
import { Toaster } from './components/ui/sonner';
import { H0Portal } from './components/H0Portal';
import { ComingSoon } from './components/ComingSoon';
import { SigFunnel } from './components/SigFunnel';
import { NutsPage } from './components/nuts/NutsPage';
import { NutsAuthProvider } from './context/NutsAuthContext';
import { CapsPage } from './components/caps/CapsPage';
import { SharkPage } from './components/shark/SharkPage';
import { QuintaPage } from './components/quinta/QuintaPage';
import { OnIaGolfPage } from './components/OnIaGolfPage';
import { CommunityHelp } from './components/community/CommunityHelp';
import { ForumHome, ForumRoom as ForumRoomView, ForumThreadView } from './components/nuts/Forum';
import { PolaroidCatalog, PolaroidItemView } from './components/community/PolaroidCatalog';
import { CommunityGalleryPage } from './components/community/CommunityGalleryPage';
import { GalleryAlbumView } from './components/community/GalleryAlbumView';
import { AmazonStore } from './components/AmazonStore';
import { MusicPlayer } from './components/MusicPlayer';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ZONA PRO
import { AuthProvider, useAuth } from './context/AuthContext';
import { ZonaProLogin } from './components/zonaPro/ZonaProLogin';
import { ZonaProDashboard } from './components/zonaPro/ZonaProDashboard';
import { ProjectDetail } from './components/zonaPro/ProjectDetail';
import { Questionnaire } from './components/zonaPro/Questionnaire';
import { AdminPanel } from './components/zonaPro/AdminPanel';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Cargando...</div>;
  }
  
  if (!user) {
    return <Navigate to="/zona-pro/login" />;
  }
  
  return children;
};

// On-IA-Golf page is now imported from './components/OnIaGolfPage'

// Wrappers for the placeholder pages so they can use translations
const CapsPlaceholder = () => {
  const { t } = useTranslation();
  return (
    <ComingSoon
      testid="caps-placeholder"
      eyebrow="CAPS · Lleida"
      title={t('h0.doors.caps')}
      subtitle={t('h0.comingSoonCaps', { defaultValue: 'Comunitat històrica dels Caps de Lleida. Aviat aquí trobaràs el fòrum, les eines i les propostes.' })}
    />
  );
};

const NutsPlaceholder = () => {
  const { t } = useTranslation();
  return (
    <ComingSoon
      testid="nuts-placeholder"
      eyebrow="NUTS · Andorra"
      title={t('h0.doors.nuts')}
      subtitle={t('h0.comingSoonNuts', { defaultValue: 'La comunitat que estem construint a Andorra. Fòrum, eines i molt més en camí.' })}
    />
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <NutsAuthProvider>
        <Routes>
          {/* H0 Portal — new homepage */}
          <Route path="/" element={<H0Portal />} />

          {/* 4 main doors */}
          <Route path="/sig" element={<SigFunnel />} />
          <Route path="/caps" element={<CapsPage />} />
          <Route path="/caps/ajuda" element={<CommunityHelp community="caps" brandLabel="🧢 CAPS · LLEIDA" />} />
          <Route path="/shark" element={<SharkPage />} />
          <Route path="/shark/ajuda" element={<CommunityHelp community="shark" brandLabel="🦈 SHARK" />} />
          <Route path="/quinta" element={<QuintaPage />} />
          <Route path="/quinta/ajuda" element={<CommunityHelp community="quinta" brandLabel="🍺 QUINTA" />} />
          <Route path="/nuts" element={<NutsPage />} />
          <Route path="/nuts/ajuda" element={<CommunityHelp community="nuts" brandLabel="🥜 NUTS · ANDORRA" />} />
          <Route path="/nuts/forum" element={<ForumHome />} />
          <Route path="/nuts/forum/r/:roomSlug" element={<ForumRoomView />} />
          <Route path="/nuts/forum/t/:threadId" element={<ForumThreadView />} />
          <Route path="/caps/forum" element={<ForumHome />} />
          <Route path="/caps/forum/r/:roomSlug" element={<ForumRoomView />} />
          <Route path="/caps/forum/t/:threadId" element={<ForumThreadView />} />
          <Route path="/shark/forum" element={<ForumHome />} />
          <Route path="/shark/forum/r/:roomSlug" element={<ForumRoomView />} />
          <Route path="/shark/forum/t/:threadId" element={<ForumThreadView />} />
          <Route path="/quinta/forum" element={<ForumHome />} />
          <Route path="/quinta/forum/r/:roomSlug" element={<ForumRoomView />} />
          <Route path="/quinta/forum/t/:threadId" element={<ForumThreadView />} />

          {/* Polaroid catalogs — list + detail for each community */}
          <Route path="/nuts/regals" element={<PolaroidCatalog community="nuts" category="gifts" />} />
          <Route path="/nuts/regals/:itemId" element={<PolaroidItemView community="nuts" category="gifts" />} />
          <Route path="/nuts/mercat" element={<PolaroidCatalog community="nuts" category="market" />} />
          <Route path="/nuts/mercat/:itemId" element={<PolaroidItemView community="nuts" category="market" />} />
          <Route path="/nuts/classes-particulars" element={<PolaroidCatalog community="nuts" category="lessons" />} />
          <Route path="/nuts/classes-particulars/:itemId" element={<PolaroidItemView community="nuts" category="lessons" />} />
          <Route path="/caps/regals" element={<PolaroidCatalog community="caps" category="gifts" />} />
          <Route path="/caps/regals/:itemId" element={<PolaroidItemView community="caps" category="gifts" />} />
          <Route path="/caps/mercat" element={<PolaroidCatalog community="caps" category="market" />} />
          <Route path="/caps/mercat/:itemId" element={<PolaroidItemView community="caps" category="market" />} />
          <Route path="/caps/classes-particulars" element={<PolaroidCatalog community="caps" category="lessons" />} />
          <Route path="/caps/classes-particulars/:itemId" element={<PolaroidItemView community="caps" category="lessons" />} />
          <Route path="/shark/regals" element={<PolaroidCatalog community="shark" category="gifts" />} />
          <Route path="/shark/regals/:itemId" element={<PolaroidItemView community="shark" category="gifts" />} />
          <Route path="/shark/mercat" element={<PolaroidCatalog community="shark" category="market" />} />
          <Route path="/shark/mercat/:itemId" element={<PolaroidItemView community="shark" category="market" />} />
          <Route path="/shark/classes-particulars" element={<PolaroidCatalog community="shark" category="lessons" />} />
          <Route path="/shark/classes-particulars/:itemId" element={<PolaroidItemView community="shark" category="lessons" />} />
          <Route path="/quinta/regals" element={<PolaroidCatalog community="quinta" category="gifts" />} />
          <Route path="/quinta/regals/:itemId" element={<PolaroidItemView community="quinta" category="gifts" />} />
          <Route path="/quinta/mercat" element={<PolaroidCatalog community="quinta" category="market" />} />
          <Route path="/quinta/mercat/:itemId" element={<PolaroidItemView community="quinta" category="market" />} />
          <Route path="/quinta/classes-particulars" element={<PolaroidCatalog community="quinta" category="lessons" />} />
          <Route path="/quinta/classes-particulars/:itemId" element={<PolaroidItemView community="quinta" category="lessons" />} />

          {/* Community photo galleries */}
          <Route path="/:community/galeria" element={<CommunityGalleryPage />} />
          <Route path="/:community/galeria/:albumId" element={<GalleryAlbumView />} />

          {/* Legacy On-IA-Golf landing (formerly at /) */}
          <Route path="/on-ia-golf" element={<OnIaGolfPage />} />

          {/* Amazon Affiliate Storefront — "De compras pel Món" */}
          <Route path="/botiga" element={<AmazonStore />} />
          <Route path="/de-compras-pel-mon" element={<AmazonStore />} />

          {/* ZONA PRO Routes */}
          <Route path="/zona-pro/login" element={<ZonaProLogin />} />
          <Route 
            path="/zona-pro/dashboard" 
            element={
              <ProtectedRoute>
                <ZonaProDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/zona-pro/proyecto/:id" 
            element={
              <ProtectedRoute>
                <ProjectDetail />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/zona-pro/cuestionario" 
            element={
              <ProtectedRoute>
                <Questionnaire />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/zona-pro/admin" 
            element={
              <ProtectedRoute>
                <AdminPanel />
              </ProtectedRoute>
            } 
          />
          
          {/* Redirect any unknown routes to home */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
        <MusicPlayer />
        <Toaster />
        </NutsAuthProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
